import type { Collection } from "dexie";

import {
  createUuid,
  PersonalDuaCreateSchema,
  PersonalDuaSchema,
  PersonalDuaUpdateSchema,
  type CreatePersonalDuaInput,
  type PersonalDua,
  type PersonalDuaStatus,
  type UpdatePersonalDuaInput,
} from "../../domain";
import type { DuaWorkspaceDatabase } from "../database";
import {
  assertLiveReferences,
  EntityNotFoundError,
  parseEntityId,
  type RepositoryOptions,
} from "./repository-utils";

export interface PersonalDuaListOptions {
  includeDeleted?: boolean;
  statuses?: readonly PersonalDuaStatus[];
  collectionId?: string;
  tagId?: string;
  personId?: string;
  sessionId?: string;
  favoriteOnly?: boolean;
  limit?: number;
  offset?: number;
  order?: "updated_desc" | "created_desc" | "sort_order";
}

export class PersonalDuaRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;
  readonly #createId: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#createId = options.createId ?? createUuid;
  }

  async list(options: PersonalDuaListOptions = {}): Promise<PersonalDua[]> {
    let query: Collection<PersonalDua, string> = this.#database.personalDuas.toCollection();
    if (options.statuses?.length === 1) {
      const status = options.statuses[0];
      if (status !== undefined) {
        query = this.#database.personalDuas.where("status").equals(status);
      }
    } else if (options.collectionId !== undefined) {
      query = this.#database.personalDuas
        .where("collectionIds")
        .equals(parseEntityId(options.collectionId));
    } else if (options.tagId !== undefined) {
      query = this.#database.personalDuas.where("tagIds").equals(parseEntityId(options.tagId));
    } else if (options.personId !== undefined) {
      query = this.#database.personalDuas.where("personIds").equals(parseEntityId(options.personId));
    } else if (options.sessionId !== undefined) {
      query = this.#database.personalDuas.where("sessionIds").equals(parseEntityId(options.sessionId));
    }

    let records = await query.toArray();
    if (options.includeDeleted !== true) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    if (options.statuses !== undefined && options.statuses.length !== 1) {
      const statuses = new Set(options.statuses);
      records = records.filter((record) => statuses.has(record.status));
    }
    if (options.collectionId !== undefined) {
      records = records.filter((record) => record.collectionIds.includes(options.collectionId as string));
    }
    if (options.tagId !== undefined) {
      records = records.filter((record) => record.tagIds.includes(options.tagId as string));
    }
    if (options.personId !== undefined) {
      records = records.filter((record) => record.personIds.includes(options.personId as string));
    }
    if (options.sessionId !== undefined) {
      records = records.filter((record) => record.sessionIds.includes(options.sessionId as string));
    }
    if (options.favoriteOnly === true) {
      records = records.filter((record) => record.favorite);
    }

    const order = options.order ?? "updated_desc";
    records.sort((left, right) => {
      if (order === "sort_order") {
        return (left.sortOrder ?? 0) - (right.sortOrder ?? 0) || right.updatedAt.localeCompare(left.updatedAt);
      }
      const field = order === "created_desc" ? "createdAt" : "updatedAt";
      return right[field].localeCompare(left[field]);
    });

    const offset = Math.max(0, options.offset ?? 0);
    const limit = Math.max(0, options.limit ?? records.length);
    return records.slice(offset, offset + limit).map((record) => PersonalDuaSchema.parse(record));
  }

  async count(options: Omit<PersonalDuaListOptions, "limit" | "offset" | "order"> = {}): Promise<number> {
    return (await this.list(options)).length;
  }

  async getById(id: string, includeDeleted = false): Promise<PersonalDua | undefined> {
    const record = await this.#database.personalDuas.get(parseEntityId(id));
    if (record === undefined || (!includeDeleted && record.deletedAt !== undefined)) {
      return undefined;
    }
    return PersonalDuaSchema.parse(record);
  }

  async create(input: CreatePersonalDuaInput): Promise<PersonalDua> {
    const values = PersonalDuaCreateSchema.parse(input);
    const timestamp = this.#now();
    const record = PersonalDuaSchema.parse({
      id: this.#createId(),
      ...values,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    return this.#database.transaction(
      "rw",
      [
        this.#database.personalDuas,
        this.#database.collections,
        this.#database.tags,
        this.#database.people,
        this.#database.sessions,
      ],
      async () => {
        await this.#assertRelationships(values);
        await this.#database.personalDuas.add(record);
        return record;
      },
    );
  }

  async quickAdd(body: string): Promise<PersonalDua> {
    return this.create({ body, status: "inbox" });
  }

  async update(id: string, input: UpdatePersonalDuaInput): Promise<PersonalDua> {
    const entityId = parseEntityId(id);
    const patch = PersonalDuaUpdateSchema.parse(input);
    return this.#database.transaction(
      "rw",
      [
        this.#database.personalDuas,
        this.#database.collections,
        this.#database.tags,
        this.#database.people,
        this.#database.sessions,
      ],
      async () => {
        const current = await this.#database.personalDuas.get(entityId);
        if (current === undefined || current.deletedAt !== undefined) {
          throw new EntityNotFoundError("PersonalDua", entityId);
        }
        await this.#assertRelationships(patch);
        const updated = PersonalDuaSchema.parse({ ...current, ...patch, updatedAt: this.#now() });
        await this.#database.personalDuas.put(updated);
        return updated;
      },
    );
  }

  async softDelete(id: string): Promise<PersonalDua> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.personalDuas, async () => {
      const current = await this.#database.personalDuas.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("PersonalDua", entityId);
      }
      if (current.deletedAt !== undefined) {
        return PersonalDuaSchema.parse(current);
      }
      const timestamp = this.#now();
      const deleted = PersonalDuaSchema.parse({
        ...current,
        deletedAt: timestamp,
        updatedAt: timestamp,
      });
      await this.#database.personalDuas.put(deleted);
      return deleted;
    });
  }

  async restore(id: string): Promise<PersonalDua> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.personalDuas, async () => {
      const current = await this.#database.personalDuas.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("PersonalDua", entityId);
      }
      const candidate = { ...current };
      delete candidate.deletedAt;
      const restored = PersonalDuaSchema.parse({ ...candidate, updatedAt: this.#now() });
      await this.#database.personalDuas.put(restored);
      return restored;
    });
  }

  async markReviewed(id: string, reviewedAt: string = this.#now()): Promise<PersonalDua> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.personalDuas, async () => {
      const current = await this.#database.personalDuas.get(entityId);
      if (current === undefined || current.deletedAt !== undefined) {
        throw new EntityNotFoundError("PersonalDua", entityId);
      }
      const updated = PersonalDuaSchema.parse({
        ...current,
        lastReviewedAt: reviewedAt,
        reviewCount: current.reviewCount + 1,
        updatedAt: this.#now(),
      });
      await this.#database.personalDuas.put(updated);
      return updated;
    });
  }

  async #assertRelationships(
    relationships: {
      collectionIds?: readonly string[] | undefined;
      tagIds?: readonly string[] | undefined;
      personIds?: readonly string[] | undefined;
      sessionIds?: readonly string[] | undefined;
    },
  ): Promise<void> {
    const checks: Promise<void>[] = [];
    if (relationships.collectionIds !== undefined) {
      checks.push(
        assertLiveReferences(this.#database.collections, relationships.collectionIds, "Collection"),
      );
    }
    if (relationships.tagIds !== undefined) {
      checks.push(assertLiveReferences(this.#database.tags, relationships.tagIds, "Tag"));
    }
    if (relationships.personIds !== undefined) {
      checks.push(assertLiveReferences(this.#database.people, relationships.personIds, "Person"));
    }
    if (relationships.sessionIds !== undefined) {
      checks.push(assertLiveReferences(this.#database.sessions, relationships.sessionIds, "Session"));
    }
    await Promise.all(checks);
  }
}
